import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
Box, Container, Paper, Typography, Table, TableBody, TableCell, TableHead, TableRow,
Tooltip, IconButton, TableContainer, Grid, Stack, Chip, Divider, CircularProgress,
Alert, TablePagination, Button, Menu, MenuItem, ListItemIcon, ListItemText
} from "@mui/material";
import { Edit, RestartAlt, FileDownload, PictureAsPdf, GridOn } from "@mui/icons-material";
import Layout from "../../Layout";
import { Bar, Doughnut } from "react-chartjs-2";
import {
Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title,
Tooltip as ChartTooltip, Legend,
} from "chart.js";
import api from "../../api";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
RISK_LEVELS, UNSUPPORTED_VALUE, canonicalRisk, normalizeTrackerStatusKey, riskBucket, statusIn,
} from "../../utils/trackerNormalization";
import {
buildUniqueSheetNames, formatDateInIST, formatDateTimeInIST, formatPeople,
} from "../../utils/reportExport";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, ChartTooltip, Legend);
const centerTextPlugin = {
id: "centerText",
beforeDraw(chart, _args, opts) {
if (!opts?.text) return;
const { ctx, chartArea } = chart;
if (!chartArea) return;
ctx.save();
ctx.font = "600 18px system-ui, -apple-system, Segoe UI, Roboto, Arial";
ctx.fillStyle = "#333";
ctx.textAlign = "center";
ctx.textBaseline = "middle";
const x = (chartArea.left + chartArea.right) / 2;
const y = (chartArea.top + chartArea.bottom) / 2;
ctx.fillText(opts.text, x, y);
ctx.restore();
},
};
ChartJS.register(centerTextPlugin);

const panelSx = { p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" };
const riskLevels = RISK_LEVELS;
const riskColors = { High: "#e53935", Medium: "#fb8c00", Low: "#43a047", [UNSUPPORTED_VALUE]: "#757575" };

const normalizeRisk = canonicalRisk;
const canonicalStatus = normalizeTrackerStatusKey;
const ACTIVE_STATUS_KEYS = new Set([
"open", "pending-for-review", "pending-for-discrepancy", "in-process", "cannot-comply", "not-complied",
]);
function toDate(v) {
const d = v ? new Date(v) : null;
return d && !isNaN(d) ? d : null;
}
function compareDatesInIST(a, b) {
const aKey = formatDateInIST(a);
const bKey = formatDateInIST(b);
if (!aKey || !bKey) return null;
return aKey.localeCompare(bKey);
}
function daysBetween(a, b) {
const aKey = formatDateInIST(a);
const bKey = formatDateInIST(b);
if (!aKey || !bKey) return null;
const [ay, am, ad] = aKey.split("-").map(Number);
const [by, bm, bd] = bKey.split("-").map(Number);
return Math.round(
    (Date.UTC(ay, am - 1, ad) - Date.UTC(by, bm - 1, bd)) /
    (1000 * 60 * 60 * 24)
);
}
const UserTrackersReportPage = () => {
const navigate = useNavigate();
const { state, key: locationKey } = useLocation();
const initialUserIds = Array.isArray(state?.userIds) ? state.userIds : (state?.userId ? [state.userId] : []);
const initialUserIdsKey = initialUserIds.join("|");
const initialStart = state?.startDate || "";
const initialEnd = state?.endDate || "";
const [userIds, setUserIds] = useState(initialUserIds);
const [startDate, setStartDate] = useState(initialStart);
const [endDate, setEndDate] = useState(initialEnd);

const [users, setUsers] = useState([]);
const [allTrackers, setAllTrackers] = useState([]);
const [error, setError] = useState("");
const [warning, setWarning] = useState("");
const [loading, setLoading] = useState(false);

const [selectedCell, setSelectedCell] = useState({ lawArea: null, bucket: null, risk: null });
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(10);

const statusChartRef = useRef(null);
const overdueChartRef = useRef(null);
const chartToPng = (ref) => {
    try {
        const chart = ref?.current;
        return chart?.toBase64Image?.("image/png", 1.0) || null;
    } catch {
        return null;
    }
};

const [exportAnchor, setExportAnchor] = useState(null);
const openExportMenu = (e) => setExportAnchor(e.currentTarget);
const closeExportMenu = () => setExportAnchor(null);

const fetchReport = useCallback(async (ids, start, end) => {
    if (!Array.isArray(ids) || ids.length === 0 || !start || !end) {
        setUsers([]); setAllTrackers([]); return;
    }
    try {
        setError(""); setWarning(""); setLoading(true);
        const { data } = await api.post("reports/getTrackersByUsers", {
            userIds: ids, startDate: start, endDate: end,
        });
        if (data?.success) {
            setUsers(data.data?.users || []);
            setAllTrackers(Array.isArray(data.data?.allTrackers) ? data.data.allTrackers : []);
            setWarning(
                data.data?.skippedUserCount
                    ? `${data.data.skippedUserCount} selected user(s) were invalid or outside your company and were excluded.`
                    : ""
            );
        } else {
            setUsers([]); setAllTrackers([]);
            setError(data?.message || "Failed to load trackers.");
        }
    } catch (e) {
        setError("Couldn't load trackers for the given users and dates.");
    } finally {
        setLoading(false);
    }
}, []);

useEffect(() => {
    const ids = initialUserIdsKey ? initialUserIdsKey.split("|") : [];
    setUserIds(ids);
    setStartDate(initialStart);
    setEndDate(initialEnd);
    setSelectedCell({ lawArea: null, bucket: null, risk: null });
    setPage(0);
    if (ids.length && initialStart && initialEnd) {
        fetchReport(ids, initialStart, initialEnd);
    } else {
        setUsers([]);
        setAllTrackers([]);
        setWarning("");
    }
}, [locationKey, initialUserIdsKey, initialStart, initialEnd, fetchReport]);

const reportEnd = useMemo(() => toDate(endDate) || new Date(), [endDate]);

const byLaw = useMemo(() => {
    const acc = {};
    for (const t of allTrackers) {
        const law = t?.lawArea || "Unspecified";
        (acc[law] ||= {
            Complied: { OnTime: 0, Delayed: 0, ProofMissing: 0 },
            CompletionMissing: 0,
            NotApplicable: 0,
            NotYetDue: 0,
            Overdue: { InProcess: { High: 0, Medium: 0, Low: 0, [UNSUPPORTED_VALUE]: 0 } },
            OtherUnsupported: 0,
        });

        const status = canonicalStatus(t?.status);
        const due = toDate(t?.dueDate);
        const completion = toDate(t?.completionDate);
        const risk = riskBucket(t?.risk);
        const proofSatisfied = t?.proof === true || t?.proofNotRequired === true;

        if (status === "complied" || status === "complied-on-time") {
            if (due && completion) {
                if (compareDatesInIST(completion, due) <= 0) acc[law].Complied.OnTime += 1;
                else acc[law].Complied.Delayed += 1;
            } else {
                acc[law].CompletionMissing += 1;
            }
            if (!proofSatisfied) acc[law].Complied.ProofMissing += 1;
            continue;
        }

        if (["delayed", "delayed-compliance", "delayed-complied"].includes(status)) {
            acc[law].Complied.Delayed += 1;
            if (!proofSatisfied) acc[law].Complied.ProofMissing += 1;
            continue;
        }

        if (status === "not-applicable") {
            acc[law].NotApplicable += 1;
            continue;
        }

        if (ACTIVE_STATUS_KEYS.has(status) && due) {
            if (compareDatesInIST(due, reportEnd) < 0) {
                acc[law].Overdue.InProcess[risk] += 1;
            } else {
                acc[law].NotYetDue += 1;
            }
        } else {
            acc[law].OtherUnsupported += 1;
        }
    }
    return acc;
}, [allTrackers, reportEnd]);

const lawAreas = useMemo(() => Object.keys(byLaw), [byLaw]);

const kpis = useMemo(() => {
    let total = 0, onTime = 0, delayed = 0, completionMissing = 0, na = 0, notYetDue = 0, overdue = 0, proofMissing = 0, other = 0;
    for (const law of lawAreas) {
        const row = byLaw[law];
        onTime += row.Complied.OnTime;
        delayed += row.Complied.Delayed;
        completionMissing += row.CompletionMissing;
        na += row.NotApplicable;
        notYetDue += row.NotYetDue;
        proofMissing += row.Complied.ProofMissing;
        overdue += riskLevels.reduce((s, r) => s + row.Overdue.InProcess[r], 0);
        other += row.OtherUnsupported;
    }
    total = onTime + delayed + completionMissing + na + notYetDue + overdue + other;
    const highOverdue = lawAreas.reduce((s, law) => s + byLaw[law].Overdue.InProcess.High, 0);
    const highPct = overdue ? Math.round((highOverdue / overdue) * 100) : 0;
    return { total, onTime, delayed, completionMissing, na, notYetDue, overdue, highOverdue, highPct, proofMissing, other };
}, [byLaw, lawAreas]);

const statusDonut = useMemo(() => {
    const labels = ["On Time", "Delayed", "Completion Date Missing", "Not Applicable", "Due on Report End Date", "Overdue (High)", "Overdue (Medium)", "Overdue (Low)", "Overdue (Unsupported Risk)", UNSUPPORTED_VALUE];
    const vals = [
        kpis.onTime, kpis.delayed, kpis.completionMissing, kpis.na, kpis.notYetDue,
        lawAreas.reduce((s, l) => s + byLaw[l].Overdue.InProcess.High, 0),
        lawAreas.reduce((s, l) => s + byLaw[l].Overdue.InProcess.Medium, 0),
        lawAreas.reduce((s, l) => s + byLaw[l].Overdue.InProcess.Low, 0),
        lawAreas.reduce((s, l) => s + (byLaw[l].Overdue.InProcess[UNSUPPORTED_VALUE] || 0), 0),
        kpis.other,
    ];
    const colors = ["#43a047", "#fb8c00", "#d81b60", "#9e9e9e", "#1e88e5", "#e53935", "#fb8c00", "#43a047", "#757575", "#424242"];
    return {
        labels,
        datasets: [{ data: vals, backgroundColor: colors, borderWidth: 0 }],
    };
}, [byLaw, lawAreas, kpis]);

const overdueRiskByLaw = useMemo(() => {
    const labels = lawAreas;
    return {
        labels,
        datasets: riskLevels.map((risk) => ({
            label: `${risk} Risk`,
            data: labels.map((law) => byLaw[law].Overdue.InProcess[risk]),
            backgroundColor: riskColors[risk],
            stack: "risk",
        })),
    };
}, [byLaw, lawAreas]);

const matrixColumns = [
    { key: "OnTime", label: "On Time", type: "Complied" },
    { key: "Delayed", label: "Delayed", type: "Complied" },
    { key: "CompletionMissing", label: "Completion Date Missing", type: "Missing" },
    { key: "NotApplicable", label: "Not Applicable", type: "NA" },
    { key: "NotYetDue", label: "Due on Report End Date", type: "NotYetDue" },
    { key: "ProofMissing", label: "Proof Missing", type: "Complied" },
    { key: "High", label: "Overdue (High)", type: "Overdue" },
    { key: "Medium", label: "Overdue (Medium)", type: "Overdue" },
    { key: "Low", label: "Overdue (Low)", type: "Overdue" },
    { key: UNSUPPORTED_VALUE, label: "Overdue (Unsupported Risk)", type: "Overdue" },
    { key: "OtherUnsupported", label: UNSUPPORTED_VALUE, type: "Other" },
];

const handleCellClick = useCallback((lawArea, col) => {
    if (!lawArea || !col) return;
    if (col.type === "Complied") {
        setSelectedCell({ lawArea, bucket: col.key, risk: null });
    } else if (col.type === "NA") {
        setSelectedCell({ lawArea, bucket: "NotApplicable", risk: null });
    } else if (col.type === "Missing") {
        setSelectedCell({ lawArea, bucket: "CompletionMissing", risk: null });
    } else if (col.type === "NotYetDue") {
        setSelectedCell({ lawArea, bucket: "NotYetDue", risk: null });
    } else if (col.type === "Other") {
        setSelectedCell({ lawArea, bucket: "OtherUnsupported", risk: null });
    } else {
        setSelectedCell({ lawArea, bucket: "Overdue", risk: col.key });
    }
    setPage(0);
}, []);

const clearSelection = useCallback(() => {
    setSelectedCell({ lawArea: null, bucket: null, risk: null });
    setPage(0);
}, []);

const detailRows = useMemo(() => {
    if (!selectedCell.lawArea) return [];
    const anchor = reportEnd;
    return (allTrackers || []).filter((t) => {
        const lawMatch = (t?.lawArea || "Unspecified") === selectedCell.lawArea;
        if (!lawMatch) return false;

        const status = canonicalStatus(t?.status);
        const due = toDate(t?.dueDate);
        const completion = toDate(t?.completionDate);
        const risk = riskBucket(t?.risk);
        const proofSatisfied = t?.proof === true || t?.proofNotRequired === true;

        switch (selectedCell.bucket) {
            case "OnTime":
                if (!["complied", "complied-on-time"].includes(status)) return false;
                if (completion && due) return compareDatesInIST(completion, due) <= 0;
                return false;
            case "Delayed":
                if (["delayed", "delayed-compliance", "delayed-complied"].includes(status)) return true;
                if (!["complied", "complied-on-time"].includes(status)) return false;
                if (completion && due) return compareDatesInIST(completion, due) > 0;
                return false;
            case "NotApplicable":
                return status === "not-applicable";
            case "CompletionMissing":
                return ["complied", "complied-on-time"].includes(status) && (!completion || !due);
            case "NotYetDue":
                return ACTIVE_STATUS_KEYS.has(status) && Boolean(due) && compareDatesInIST(due, anchor) >= 0;
            case "ProofMissing":
                return ["complied", "complied-on-time", "delayed", "delayed-compliance", "delayed-complied"].includes(status) && !proofSatisfied;
            case "Overdue":
                if (["complied", "complied-on-time", "delayed", "delayed-compliance", "delayed-complied", "not-applicable"].includes(status)) return false;
                if (!due) return false;
                return (compareDatesInIST(due, anchor) < 0) && (risk === selectedCell.risk);
            case "OtherUnsupported":
                return !ACTIVE_STATUS_KEYS.has(status) || !due;
            default:
                return false;
        }
    });
}, [selectedCell, allTrackers, reportEnd]);

const sortByDueDateAsc = (arr) =>
    arr.slice().sort((a, b) => {
        const da = a?.dueDate ? new Date(a.dueDate) : null;
        const db = b?.dueDate ? new Date(b.dueDate) : null;
        if (da && db) return da - db;
        if (da && !db) return -1;
        if (!da && db) return 1;
        return String(a?.uniqueIdentifier || "").localeCompare(String(b?.uniqueIdentifier || ""));
    });

const buildGroupedSorted = (useFiltered) => {
    const src = useFiltered ? detailRows : allTrackers;
    const groups = {};
    src.forEach((t) => {
        const la = t?.lawArea || "Unspecified";
        (groups[la] ||= []).push(t);
    });
    const orderedLawAreas = Object.keys(groups).sort((a, b) => a.localeCompare(b));
    orderedLawAreas.forEach((la) => {
        groups[la] = sortByDueDateAsc(groups[la]);
    });
    return { groups, orderedLawAreas };
};

const mapTrackerToRow = (t) => {
    return {
        UIN: t?.uniqueIdentifier || "",
        Location: t?.location || "",
        "Compliance Activity": t?.complianceActivity || "",
        "Law Area": t?.lawArea || "",
        Description: t?.description || "",
        Category: Array.isArray(t?.category) ? t.category.join(", ") : t?.category || "",
        "Act or Rule": t?.actOrRule || "",
        Periodicity: t?.periodicity || "",
        Consequence: t?.consequence || "",
        "Source URL": t?.sourceURL || "",
        "Due Date (IST)": formatDateInIST(t?.dueDate),
        "Completion Date (IST)": formatDateInIST(t?.completionDate),
        Risk: normalizeRisk(t?.risk),
        Status: t?.status || "",
        "Normalized Status": canonicalStatus(t?.status),
        "Proof Submitted": t?.proof ? "Yes" : "No",
        "Proof Not Required": t?.proofNotRequired ? "Yes" : "No",
        "Tracker Owner(s)": formatPeople(t?.trackerOwner),
        "Tracker Manager(s)": formatPeople(t?.trackerManager),
        "Legal Update Link": t?.legalUpdateLink || "",
    };
};

const buildSummaryMatrixRows = () => {
    const rows = [];
    Object.entries(byLaw).forEach(([law, vals]) => {
        const OnTime = vals.Complied.OnTime || 0;
        const Delayed = vals.Complied.Delayed || 0;
        const CompletionMissing = vals.CompletionMissing || 0;
        const NotApplicable = vals.NotApplicable || 0;
        const NotYetDue = vals.NotYetDue || 0;
        const ProofMissing = vals.Complied.ProofMissing || 0;
        const High = vals.Overdue.InProcess.High || 0;
        const Medium = vals.Overdue.InProcess.Medium || 0;
        const Low = vals.Overdue.InProcess.Low || 0;
        const UnsupportedRisk = vals.Overdue.InProcess[UNSUPPORTED_VALUE] || 0;
        const OtherUnsupported = vals.OtherUnsupported || 0;
        const Total = OnTime + Delayed + CompletionMissing + NotApplicable + NotYetDue + High + Medium + Low + UnsupportedRisk + OtherUnsupported;
        if (Total > 0) rows.push({ "Law Area": law, OnTime, Delayed, CompletionMissing, NotApplicable, NotYetDue, ProofMissing, High, Medium, Low, UnsupportedRisk, OtherUnsupported, Total });
    });
    return rows;
};

const makeFilename = (ext, filtered) => {
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    const scope = filtered ? "filtered" : "all";
    return `user_trackers_report_${scope}_${ts}.${ext}`;
};

const exportXLSX = (filtered) => {
    const { groups, orderedLawAreas } = buildGroupedSorted(filtered);
    const wb = XLSX.utils.book_new();
    const mainSheetName = filtered ? "Filtered (Grouped)" : "All (Grouped)";
    const lawAreaSheetNames = buildUniqueSheetNames(orderedLawAreas, [
        mainSheetName,
        "Summary Matrix",
        "Meta",
    ]);

    const flatRows = [];
    orderedLawAreas.forEach((la) => {
        groups[la].forEach((t) => flatRows.push(mapTrackerToRow(t)));
    });
    const wsMain = XLSX.utils.json_to_sheet(flatRows);
    XLSX.utils.book_append_sheet(wb, wsMain, mainSheetName);

    orderedLawAreas.forEach((la) => {
        const ws = XLSX.utils.json_to_sheet(groups[la].map(mapTrackerToRow));
        XLSX.utils.book_append_sheet(wb, ws, lawAreaSheetNames[la]);
    });

    const wsSummary = XLSX.utils.json_to_sheet(buildSummaryMatrixRows());
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary Matrix");

    const metaSheet = XLSX.utils.aoa_to_sheet([
        ["Users", users.map((user) => `${user.name || "-"}${user.role ? ` (${user.role})` : ""}`).join("; ") || "-"],
        ["Start Date", startDate || "-"],
        ["End Date", endDate || "-"],
        ["Generated (IST)", formatDateTimeInIST()],
        [],
        ["KPI", "Value"],
        ["Total Items", kpis.total],
        ["On Time", kpis.onTime],
        ["Delayed", kpis.delayed],
        ["Completion Date Missing", kpis.completionMissing],
        ["Not Applicable", kpis.na],
        ["Due on Report End Date", kpis.notYetDue],
        ["Overdue (All)", kpis.overdue],
        ["Overdue High", kpis.highOverdue],
        ["High Overdue %", `${kpis.highPct}%`],
        ["Proof Missing (Complied)", kpis.proofMissing],
        [UNSUPPORTED_VALUE, kpis.other],
    ]);
    XLSX.utils.book_append_sheet(wb, metaSheet, "Meta");

    XLSX.writeFile(wb, makeFilename("xlsx", filtered));
};

const exportPDF = (filtered) => {
    const { groups, orderedLawAreas } = buildGroupedSorted(filtered);
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

    doc.setFontSize(16);
    doc.text("User Trackers Report", 40, 40);
    doc.setFontSize(10);
    const metaLines = [
        `Users: ${users.map((user) => `${user.name || "-"}${user.role ? ` (${user.role})` : ""}`).join("; ") || "-"}`,
        `Range: ${startDate || "-"} to ${endDate || "-"}`,
        `Generated: ${formatDateTimeInIST()} IST`,
        `Scope: ${filtered ? "Filtered selection" : "All trackers"}`,
    ];
    metaLines.forEach((ln, i) => doc.text(ln, 40, 60 + i * 14));

    const chartsY = 60 + metaLines.length * 14 + 16;
    const pageW = doc.internal.pageSize.getWidth();
    const left = 40;
    const gap = 24;
    const colW = (pageW - left * 2 - gap) / 2;
    const imgH = 220;

    const imgStatus = chartToPng(statusChartRef);
    const imgOverdue = chartToPng(overdueChartRef);

    doc.setFontSize(12);
    doc.text("Charts", left, chartsY);

    let y = chartsY + 14;
    if (imgStatus) {
        doc.addImage(imgStatus, "PNG", left, y, colW, imgH);
    }
    if (imgOverdue) {
        doc.addImage(imgOverdue, "PNG", left + colW + gap, y, colW, imgH);
    }

    doc.addPage();
    let startY = 40;

    const columns = [
        "UIN",
        "Location",
        "Activity",
        "Tracker Details",
        "Dates (IST)",
        "Status / Risk",
        "Owners / Managers",
        "Legal Update Link",
    ];

    orderedLawAreas.forEach((la, idx) => {
        if (idx > 0 && startY > doc.internal.pageSize.getHeight() - 120) {
            doc.addPage();
            startY = 40;
        }
        doc.setFontSize(12);
        doc.text(`Law Area: ${la}`, 40, startY);
        startY += 8;

        const rows = groups[la].map(mapTrackerToRow);

        autoTable(doc, {
            startY,
            head: [columns],
            body: rows.map((row) => [
                row.UIN,
                row.Location,
                row["Compliance Activity"],
                [
                    `Description: ${row.Description || "-"}`,
                    `Category: ${row.Category || "-"}`,
                    `Act/Rule: ${row["Act or Rule"] || "-"}`,
                    `Periodicity: ${row.Periodicity || "-"}`,
                    `Consequence: ${row.Consequence || "-"}`,
                    `Source: ${row["Source URL"] || "-"}`,
                ].join("\n"),
                [
                    `Due: ${row["Due Date (IST)"] || "-"}`,
                    `Completed: ${row["Completion Date (IST)"] || "-"}`,
                ].join("\n"),
                [
                    `Status: ${row.Status || "-"}`,
                    `Risk: ${row.Risk || "-"}`,
                    `Proof submitted: ${row["Proof Submitted"]}`,
                    `Proof not required: ${row["Proof Not Required"]}`,
                ].join("\n"),
                [
                    `Owners: ${row["Tracker Owner(s)"] || "-"}`,
                    `Managers: ${row["Tracker Manager(s)"] || "-"}`,
                ].join("\n"),
                row["Legal Update Link"],
            ]),
            styles: { fontSize: 5.5, cellPadding: 2, overflow: "linebreak", valign: "top" },
            headStyles: { fillColor: [30, 136, 229], fontSize: 6, halign: "center" },
            columnStyles: {
                0: { cellWidth: 42 },
                1: { cellWidth: 44 },
                2: { cellWidth: 68 },
                3: { cellWidth: 170 },
                4: { cellWidth: 68 },
                5: { cellWidth: 74 },
                6: { cellWidth: 100 },
                7: { cellWidth: 145 },
            },
            didDrawPage: (data) => {
                const str = `Page ${doc.internal.getNumberOfPages()}`;
                doc.setFontSize(9);
                doc.text(str, data.settings.margin.left, doc.internal.pageSize.getHeight() - 10);
            },
        });

        startY = (doc.lastAutoTable?.finalY || startY) + 16;
    });

    doc.save(makeFilename("pdf", filtered));
};

return (
    <Layout title="User Report">
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>User Trackers Report</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label="High" size="small" sx={{ bgcolor: "#fdecea", color: "#b71c1c" }} />
                    <Chip label="Medium" size="small" sx={{ bgcolor: "#fff3e0", color: "#e65100" }} />
                    <Chip label="Low" size="small" sx={{ bgcolor: "#e8f5e9", color: "#1b5e20" }} />
                    <Button variant="outlined" startIcon={<FileDownload />} onClick={openExportMenu} sx={{ ml: 1 }}>
                        Export
                    </Button>
                    <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={closeExportMenu}>
                        <MenuItem onClick={() => { exportXLSX(false); closeExportMenu(); }}>
                            <ListItemIcon><GridOn fontSize="small" /></ListItemIcon>
                            <ListItemText>Excel (.xlsx) — All trackers</ListItemText>
                        </MenuItem>
                        <MenuItem disabled={!detailRows.length} onClick={() => { exportXLSX(true); closeExportMenu(); }}>
                            <ListItemIcon><GridOn fontSize="small" /></ListItemIcon>
                            <ListItemText>Excel (.xlsx) — Filtered (Details)</ListItemText>
                        </MenuItem>
                        <Divider sx={{ my: 0.5 }} />
                        <MenuItem onClick={() => { exportPDF(false); closeExportMenu(); }}>
                            <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                            <ListItemText>PDF — All trackers (incl. charts)</ListItemText>
                        </MenuItem>
                        <MenuItem disabled={!detailRows.length} onClick={() => { exportPDF(true); closeExportMenu(); }}>
                            <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                            <ListItemText>PDF — Filtered (incl. charts)</ListItemText>
                        </MenuItem>
                    </Menu>
                </Stack>
            </Stack>

            {!!error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {!!warning && <Alert severity="warning" sx={{ mb: 2 }}>{warning}</Alert>}

            {(userIds?.length && startDate && endDate) ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Users: {userIds.length} • Range: {startDate} → {endDate} (IST)
                    <Button size="small" sx={{ ml: 2 }} onClick={() => fetchReport(userIds, startDate, endDate)}>Refresh</Button>
                </Alert>
            ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Provide <strong>userIds</strong>, <strong>startDate</strong>, and <strong>endDate</strong> via route state or wire inputs to call the API.
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : allTrackers.length === 0 ? (
                <Paper variant="outlined" sx={panelSx}>
                    <Typography variant="subtitle1" color="text.secondary" align="center">
                        No trackers for the current selection.
                    </Typography>
                </Paper>
            ) : (
                <>
                    <Grid container spacing={3} sx={{ mb: 1 }}>
                        {[
                            { label: "Total Items", value: kpis.total },
                            { label: "On Time", value: kpis.onTime },
                            { label: "Delayed", value: kpis.delayed },
                            { label: "Completion Date Missing", value: kpis.completionMissing },
                            { label: "Not Applicable", value: kpis.na },
                            { label: "Due on Report End Date", value: kpis.notYetDue },
                            { label: "Overdue (All)", value: kpis.overdue },
                            { label: "Overdue High", value: kpis.highOverdue },
                            { label: "High Overdue %", value: `${kpis.highPct}%` },
                            { label: "Proof Missing (Complied)", value: kpis.proofMissing },
                            { label: UNSUPPORTED_VALUE, value: kpis.other },
                        ].map((k, i) => (
                            <Grid key={i} item xs={12} sm={6} md={3}>
                                <Paper variant="outlined" sx={panelSx}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>{k.value}</Typography>
                                    <Typography variant="body2" color="text.secondary">{k.label}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={5} lg={4}>
                            <Paper variant="outlined" sx={{ ...panelSx, height: 420 }}>
                                <Typography variant="h6" gutterBottom>Status Distribution</Typography>
                                <Box sx={{ position: "relative", height: 330 }}>
                                    <Doughnut
                                        ref={statusChartRef}
                                        data={statusDonut}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 10, boxHeight: 10 } },
                                                title: { display: true, text: "On Time / Delayed / NA / Overdue risk" },
                                                centerText: { text: String(kpis.total) },
                                                tooltip: {
                                                    callbacks: {
                                                        label: (ctx) => {
                                                            const value = ctx.raw ?? 0;
                                                            const total = (ctx.dataset.data || []).reduce((a, b) => a + b, 0) || 1;
                                                            const pct = Math.round((value / total) * 100);
                                                            return `${ctx.label}: ${value} (${pct}%)`;
                                                        },
                                                    },
                                                },
                                            },
                                            cutout: "65%",
                                        }}
                                        onClick={(_e, els) => {
                                            if (!els?.length) return;
                                        }}
                                    />
                                </Box>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={7} lg={8}>
                            <Paper variant="outlined" sx={{ ...panelSx, height: 320 }}>
                                <Typography variant="h6" gutterBottom>Overdue Risk by Law Area</Typography>
                                <Box sx={{ position: "relative", height: 240 }}>
                                    <Bar
                                        ref={overdueChartRef}
                                        data={overdueRiskByLaw}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 10, boxHeight: 10 } },
                                                title: { display: true, text: "Stacked overdue counts (by risk)" },
                                            },
                                            scales: {
                                                x: { stacked: true, ticks: { autoSkip: true, maxRotation: 0 } },
                                                y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
                                            },
                                        }}
                                        onClick={(_e, els) => {
                                            if (!els?.length) return;
                                            const { index, datasetIndex } = els[0];
                                            const law = overdueRiskByLaw.labels[index];
                                            const risk = (overdueRiskByLaw.datasets[datasetIndex].label || "").replace(" Risk", "");
                                            setSelectedCell({ lawArea: law, bucket: "Overdue", risk });
                                            setPage(0);
                                        }}
                                    />
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Paper variant="outlined" sx={{ ...panelSx, mt: 3, overflow: "hidden" }}>
                        <Typography variant="h6" sx={{ mb: 1.5 }}>Summary Matrix</Typography>
                        <TableContainer sx={{ maxHeight: { xs: 360, md: "50vh" } }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, position: "sticky", left: 0, zIndex: 3, bgcolor: "background.paper", minWidth: 220 }}>
                                            Law Area
                                        </TableCell>
                                        {matrixColumns.map((c) => (
                                            <TableCell key={c.key} align="center" sx={{ fontWeight: 700, minWidth: 140 }}>{c.label}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lawAreas.map((law) => {
                                        const row = byLaw[law];
                                        const getVal = (c) => {
                                            if (c.type === "Complied") return row.Complied[c.key] || 0;
                                            if (c.type === "NA") return row.NotApplicable || 0;
                                            if (c.type === "Missing") return row.CompletionMissing || 0;
                                            if (c.type === "NotYetDue") return row.NotYetDue || 0;
                                            if (c.type === "Other") return row.OtherUnsupported || 0;
                                            return row.Overdue.InProcess[c.key] || 0;
                                        };
                                        return (
                                            <TableRow hover key={law}>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 600, position: "sticky", left: 0, zIndex: 2,
                                                        bgcolor: "background.paper"
                                                    }}
                                                >
                                                    {law}
                                                </TableCell>
                                                {matrixColumns.map((c) => {
                                                    const val = getVal(c);
                                                    const alpha =
                                                        c.type === "Overdue" ? Math.min(val / 10, 0.25)
                                                            : c.key === "Delayed" ? Math.min(val / 10, 0.2)
                                                                : c.key === "OnTime" ? Math.min(val / 10, 0.15)
                                                                    : c.key === "ProofMissing" ? Math.min(val / 10, 0.15)
                                                                        : Math.min(val / 10, 0.15);

                                                    const bg =
                                                        c.type === "Overdue"
                                                            ? `rgba(${c.key === "High" ? "229,57,53" : c.key === "Medium" ? "251,140,0" : "67,160,71"},${alpha})`
                                                            : c.key === "Delayed"
                                                                ? `rgba(251,140,0,${alpha})`
                                                                : c.key === "OnTime"
                                                                    ? `rgba(67,160,71,${alpha})`
                                                                    : c.key === "ProofMissing"
                                                                        ? `rgba(233,30,99,${alpha})`
                                                                        : `rgba(158,158,158,${alpha})`;

                                                    return (
                                                        <Tooltip key={`${law}-${c.key}`} title={`${law} • ${c.label} — ${val}`} arrow>
                                                            <TableCell
                                                                align="center"
                                                                onClick={() => handleCellClick(law, c)}
                                                                sx={(theme) => ({
                                                                    cursor: "pointer",
                                                                    bgcolor: bg,
                                                                    "&:hover": { bgcolor: theme.palette.action.hover },
                                                                    fontWeight: val > 0 ? 600 : 400,
                                                                    color: val > 0 ? "text.primary" : "text.secondary",
                                                                })}
                                                            >
                                                                {val}
                                                            </TableCell>
                                                        </Tooltip>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {(selectedCell.lawArea) && (
                        <Paper variant="outlined" sx={{ ...panelSx, mt: 3 }}>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1.5}
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                justifyContent="space-between"
                            >
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Typography variant="h6">Details — {selectedCell.lawArea}</Typography>
                                    {selectedCell.bucket && (
                                        <Chip label={selectedCell.bucket === "Overdue" ? `Overdue (${selectedCell.risk})` : selectedCell.bucket} size="small" />
                                    )}
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {detailRows.length} item{detailRows.length === 1 ? "" : "s"}
                                    </Typography>
                                    <Button size="small" startIcon={<RestartAlt />} onClick={clearSelection}>
                                        Reset filters
                                    </Button>
                                </Stack>
                            </Stack>

                            <Divider sx={{ my: 1.5 }} />

                            <TableContainer sx={{ maxHeight: 440 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>UIN</TableCell>
                                            <TableCell>Location</TableCell>
                                            <TableCell>Compliance Activity</TableCell>
                                            <TableCell>Law Area</TableCell>
                                            <TableCell>Act/Rule</TableCell>
                                            <TableCell>Due</TableCell>
                                            <TableCell>Completed</TableCell>
                                            <TableCell>Δ Days</TableCell>
                                            <TableCell>Risk</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Proof</TableCell>
                                            <TableCell align="center">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detailRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((t, idx) => {
                                            const due = toDate(t?.dueDate);
                                            const comp = toDate(t?.completionDate);
                                            const delta =
                                                comp && due ? daysBetween(comp, due)
                                                    : (due && reportEnd) && !statusIn(t?.status, ["complied", "complied on time", "delayed", "delayed compliance", "delayed complied", "not applicable"])
                                                        ? daysBetween(reportEnd, due)
                                                        : null;
                                            return (
                                                <TableRow hover key={`${t?.uniqueIdentifier || "row"}-${idx}`} sx={{ "&:nth-of-type(odd)": { bgcolor: "action.hover" } }}>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{t?.uniqueIdentifier || "-"}</TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{t?.location || "-"}</TableCell>
                                                    <TableCell>{t?.complianceActivity || "-"}</TableCell>
                                                    <TableCell>{t?.lawArea || "-"}</TableCell>
                                                    <TableCell>{t?.actOrRule || "-"}</TableCell>
                                                    <TableCell>{formatDateInIST(due) || "-"}</TableCell>
                                                    <TableCell>{formatDateInIST(comp) || "-"}</TableCell>
                                                    <TableCell>{typeof delta === "number" ? delta : "-"}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={normalizeRisk(t?.risk)}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: normalizeRisk(t?.risk) === "High" ? "#fdecea" :
                                                                    normalizeRisk(t?.risk) === "Medium" ? "#fff3e0" : "#e8f5e9",
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{t?.status || "-"}</TableCell>
                                                    <TableCell>{t?.proof ? "Yes" : "No"}</TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="Open details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => navigate(`/Edit/${encodeURIComponent(t?.uniqueIdentifier || "")}`)}
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                count={detailRows.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(_, p) => setPage(p)}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            />
                        </Paper>
                    )}
                </>
            )}
        </Container>
    </Layout>
);
};

export default UserTrackersReportPage;